import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface ConfirmDestructiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Titulo do modal, ex.: "Desativar base de conhecimento". */
  title: string;
  /** Descricao curta sobre as consequencias da acao. */
  description?: React.ReactNode;
  /** Nome exato que o usuario precisa digitar para confirmar. */
  resourceName: string;
  /** Tipo do recurso (ex.: "base", "MCP", "time", "usuario"). Usado nos textos auxiliares. */
  resourceKind?: string;
  /** Texto do botao de confirmacao (default: traducao de ``actions.confirm``). */
  confirmLabel?: string;
  /** Callback executado quando o usuario confirma. */
  onConfirm: () => void | Promise<void>;
  /** True enquanto a mutacao esta em andamento. */
  isPending?: boolean;
}

export function ConfirmDestructiveDialog({
  open,
  onOpenChange,
  title,
  description,
  resourceName,
  resourceKind,
  confirmLabel,
  onConfirm,
  isPending = false,
}: ConfirmDestructiveDialogProps) {
  const { t } = useTranslation("common");
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const matches = typed.trim() === resourceName.trim();
  const placeholder = resourceKind
    ? t("confirmDialog.placeholderWithKind", { kind: resourceKind })
    : t("confirmDialog.placeholder");
  const finalConfirmLabel = confirmLabel ?? t("actions.confirm");

  async function handleConfirm() {
    if (!matches || isPending) return;
    await onConfirm();
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>

        <div className="space-y-2">
          <p className="text-sm text-ink-700">
            <Trans
              i18nKey="confirmDialog.prompt"
              t={t}
              values={{ name: resourceName }}
              components={{
                code: (
                  <span className="rounded bg-canvas px-1.5 py-0.5 font-mono text-[12px] text-ink-950" />
                ),
              }}
            />
          </p>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={placeholder}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches && !isPending) {
                e.preventDefault();
                void handleConfirm();
              }
            }}
          />
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={!matches || isPending}
          >
            {isPending ? t("states.processing") : finalConfirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
